import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useStore } from '@nanostores/react';
import { $authFlow } from '../store/auth';
import { SectionContainerTransparent, StyledTextInput } from './AuthCommonStyledComponents';
import { RadioButton } from '../ui/RadioButton';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { $isPrivacyOpen, $isTermsIsOpen } from '../store/modals';
import { signUp } from 'aws-amplify/auth';

const NameContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  flex-direction: row;
  @media (max-width: 791px) {
    flex-direction: column;
    width: 100%;
    align-items: center;
  }
`
export const SignupForm: FC = () => {
  const authFlowStore = useStore($authFlow)
  const [internalState, setInternalState] = useState<'init' | 'loaded'>('init')

  const [email, setEmail] = useState<string>("")
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameValidity, setFirstNameValidity] = React.useState<boolean>(
    false
  )
  const [lastNameValidity, setLastNameValidity] = React.useState<boolean>(false)
  const [signupTouched, setSignupTouched] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState<boolean>(
    false
  )

  const isTermsError = useMemo(() => {
    return signupTouched && !termsAccepted
  }, [termsAccepted, signupTouched])

  const isPrivacyError = useMemo(() => {
    return signupTouched && !privacyPolicyAccepted
  }, [privacyPolicyAccepted, signupTouched])

  useEffect(() => {
    if (authFlowStore.state === 'signup' && authFlowStore.persistentData.email) {
      setInternalState('loaded')
      setEmail(authFlowStore.persistentData.email)
    }
  }, [authFlowStore]);

  const onStartSignUp = useCallback(async () => {
    setSignupTouched(true)
    if (
      !email ||
      email.length === 0 ||
      !firstNameValidity ||
      !lastNameValidity ||
      !termsAccepted ||
      !privacyPolicyAccepted
    ) {
      console.error("Some fields is required for sign up")
      return
    }
    // Sign up using an email address
    setIsLoading(true)
    try {
      const { nextStep: signUpNextStep } = await signUp({
        username: email,
        options: {
          userAttributes: {
            email,
            given_name: firstName, // Maps to Cognito standard attribute 'given_name'
            family_name: lastName // Maps to Cognito standard attribute 'family_name'
          },
          autoSignIn: {
            authFlowType: "USER_AUTH"
          }
        }
      })

      console.log("signUpNextStep.signUpStep", signUpNextStep.signUpStep)
      if (signUpNextStep.signUpStep === "DONE") {
        console.log(`SignUp Complete`)
      }

      if (signUpNextStep.signUpStep === "CONFIRM_SIGN_UP") {
        $authFlow.setKey('validationFrom', 'signup')
        $authFlow.setKey('state', 'confirm')
        console.log(
          `Code Delivery Medium: ${signUpNextStep.codeDeliveryDetails.deliveryMedium}`
        )
        console.log(
          `Code Delivery Destination: ${signUpNextStep.codeDeliveryDetails.destination}`
        )
      }
    } catch (error) {
      // @ts-ignore
      if (error?.name === "UserLambdaValidationException") {
        console.error("Something went wrong during sign up:", error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    firstName,
    firstNameValidity,
    email,
    lastName,
    lastNameValidity,
    termsAccepted,
    privacyPolicyAccepted
  ])

  if (internalState === 'init') {
    return (
      <></>
    )
  }

  return (
    <SectionContainerTransparent title={'Create your account'}>
      <>
        <StyledTextInput
          label="Email"
          type="email"
          name={"email"}
          placeholder="Enter your email address..."
          value={email}
          readonly={true}
          required
          transparent={false}
          onChange={() => {}}
        />
        <NameContainer>
          <StyledTextInput
            label="First Name"
            name={"firstName"}
            placeholder="Enter your first name..."
            onChange={newFirstName => {
              setFirstName(newFirstName)
            }}
            value={firstName}
            required
            onValidityChange={setFirstNameValidity}
            error={
              signupTouched && !firstNameValidity
                ? "First name is required"
                : undefined
            }
            transparent={false}
          />
          <StyledTextInput
            label="Last Name"
            name={"lastName"}
            placeholder="Enter your last name..."
            onChange={newLastName => {
              setLastName(newLastName)
            }}
            value={lastName}
            required
            onValidityChange={setLastNameValidity}
            error={
              signupTouched && !lastNameValidity
                ? "Last name is required"
                : undefined
            }
            transparent={false}
          />
        </NameContainer>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            marginTop: "10px"
          }}
        >
          <RadioButton
            options={[
              {
                id: "terms",
                main: (
                  <CheckIcon
                    style={{
                      width: "26px",
                      opacity: termsAccepted ? 1 : 0.2
                    }}
                  />
                )
              }
            ]}
            selected={termsAccepted ? "terms" : ""}
            onSelect={() => {
              setTermsAccepted(prev => !prev)
            }}
          />
          <div onClick={() => setTermsAccepted(prev => !prev)}>
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "1rem",
                            fontWeight: "bold",
                            cursor: "pointer",
                            color: isTermsError ? "#ea3053" : "inherit"
                          }}
                        >
                          I accept the{" "}
                          <span
                            style={{ textDecoration: "underline" }}
                            onClick={e => {
                              e.preventDefault()
                              e.stopPropagation()
                              $isTermsIsOpen.set(true)
                            }}
                          >
                            Terms of Conditions
                          </span>
                        </span>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            marginTop: "10px"
          }}
        >
          <RadioButton
            options={[
              {
                id: "privacy",
                main: (
                  <CheckIcon
                    style={{
                      width: "26px",
                      opacity: privacyPolicyAccepted ? 1 : 0.2
                    }}
                  />
                )
              }
            ]}
            selected={privacyPolicyAccepted ? "privacy" : ""}
            onSelect={() => {
              setPrivacyPolicyAccepted(prev => !prev)
            }}
          />
          <div
            onClick={() => setPrivacyPolicyAccepted(prev => !prev)}
          >
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "1rem",
                            fontWeight: "bold",
                            cursor: "pointer",
                            color: isPrivacyError ? "#ea3053" : "inherit"
                          }}
                        >
                          I accept the{" "}
                          <span
                            style={{ textDecoration: "underline" }}
                            onClick={e => {
                              e.preventDefault()
                              e.stopPropagation()
                              $isPrivacyOpen.set(true)
                            }}
                          >
                            Privacy Policy
                          </span>
                        </span>
          </div>
        </div>
        <Button
          large
          disabled={isLoading}
          onClick={async () => {
            await onStartSignUp()
          }}
        >
          {"Sign up"}
        </Button>
      </>
    </SectionContainerTransparent>
  )
}